import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Users, MapPin, Activity, Settings, Trash2, AlertCircle, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import { Team, Module, TeamPlan, User } from '../../types';

interface TeamWithDetails extends Team {
  stats: {
    userCount: number;
    posCount: number;
    visitCount: number;
  };
  enabledModules: string[];
  users: User[];
}

export const TeamDetail: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const navigate = useNavigate();
  const [team, setTeam] = useState<TeamWithDetails | null>(null);
  const [modules, setModules] = useState<Module[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    billing_email: '',
    plan: 'starter' as TeamPlan,
    max_users: 5,
    max_pos: 500,
    allow_super_admin_data_access: false,
    is_active: true,
  });

  // Module selection
  const [selectedModules, setSelectedModules] = useState<string[]>([]);

  useEffect(() => {
    fetchTeamDetails();
    fetchModules();
  }, [teamId]);

  const fetchTeamDetails = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/admin/teams/${teamId}`);
      const teamData = response.data.team;
      setTeam(teamData);
      setFormData({
        name: teamData.name,
        billing_email: teamData.billing_email || '',
        plan: teamData.plan,
        max_users: teamData.max_users,
        max_pos: teamData.max_pos,
        allow_super_admin_data_access: teamData.allow_super_admin_data_access,
        is_active: teamData.is_active,
      });
      setSelectedModules(teamData.enabledModules || []);
    } catch (err) {
      console.error('Failed to fetch team:', err);
      setError('Failed to load team details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchModules = async () => {
    try {
      const response = await api.get('/admin/modules');
      setModules(response.data.modules);
    } catch (err) {
      console.error('Failed to fetch modules:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsSubmitting(true);

    try {
      // Update team settings
      await api.patch(`/admin/teams/${teamId}`, formData);
      // Update modules
      await api.put(`/admin/teams/${teamId}/modules`, { module_ids: selectedModules });
      setSuccessMessage('Team updated successfully');
      fetchTeamDetails();
    } catch (err: any) {
      console.error('Failed to update team:', err);
      setError(err.response?.data?.error || 'Failed to update team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModuleToggle = (moduleId: string, isCore: boolean) => {
    if (isCore) return; // Can't toggle core modules
    
    setSelectedModules(prev => {
      if (prev.includes(moduleId)) {
        return prev.filter(id => id !== moduleId);
      }
      return [...prev, moduleId];
    });
  };

  const handleDelete = async () => {
    if (!confirm(`Are you sure you want to delete team "${team?.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await api.delete(`/admin/teams/${teamId}`);
      navigate('/admin/teams');
    } catch (err: any) {
      console.error('Failed to delete team:', err);
      setError(err.response?.data?.error || 'Failed to delete team');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="px-4 py-6 max-w-4xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">Team not found</p>
        </div>
      </div>
    );
  }

  // Group modules by category
  const modulesByCategory = modules.reduce((acc, module) => {
    if (!acc[module.category]) acc[module.category] = [];
    acc[module.category].push(module);
    return acc;
  }, {} as Record<string, Module[]>);

  const getPlanBadgeColor = (plan: string) => {
    switch (plan) {
      case 'enterprise': return 'bg-purple-100 text-purple-800';
      case 'growth': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/admin/teams')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{team.name}</h1>
            <p className="text-sm text-gray-500 mt-1">{team.slug}</p>
          </div>
        </div>
        <button
          onClick={handleDelete}
          className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          <Trash2 size={18} />
          Delete Team
        </button>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3 text-red-700">
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {successMessage && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3 text-green-700">
          <CheckCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* Stats Overview */}
      <section className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">Overview</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Users size={24} className="text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Users</p>
              <p className="text-xl font-semibold">
                {team.stats.userCount} / {team.max_users}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-green-100 rounded-lg">
              <MapPin size={24} className="text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Points of Sale</p>
              <p className="text-xl font-semibold">
                {team.stats.posCount} / {team.max_pos}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="p-3 bg-purple-100 rounded-lg">
              <Activity size={24} className="text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">Total Visits</p>
              <p className="text-xl font-semibold">{team.stats.visitCount}</p>
            </div>
          </div>
        </div>
      </section>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Team Settings */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Settings size={20} />
            Team Settings
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team Name</label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Billing Email</label>
              <input
                type="email"
                value={formData.billing_email}
                onChange={(e) => setFormData(prev => ({ ...prev, billing_email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan</label>
              <select
                value={formData.plan}
                onChange={(e) => setFormData(prev => ({ ...prev, plan: e.target.value as TeamPlan }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max Users</label>
              <input
                type="number"
                required
                min={1}
                value={formData.max_users}
                onChange={(e) => setFormData(prev => ({ ...prev, max_users: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Max PoS</label>
              <input
                type="number"
                required
                min={1}
                value={formData.max_pos}
                onChange={(e) => setFormData(prev => ({ ...prev, max_pos: parseInt(e.target.value) }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div className="mt-4 space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.allow_super_admin_data_access}
                onChange={(e) => setFormData(prev => ({ ...prev, allow_super_admin_data_access: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Allow super admin to view team data</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">Team is active</span>
            </label>
          </div>
        </section>

        {/* Modules */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <h2 className="text-lg font-semibold mb-4">Enabled Modules</h2>
          <div className="space-y-6">
            {Object.entries(modulesByCategory).map(([category, categoryModules]) => (
              <div key={category}>
                <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryModules.map((module) => (
                    <label
                      key={module.id}
                      className={`flex items-start gap-3 p-3 rounded-lg border ${
                        module.is_core 
                          ? 'bg-gray-50 border-gray-200 cursor-not-allowed' 
                          : 'bg-white border-gray-200 hover:border-blue-300 cursor-pointer'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedModules.includes(module.id) || module.is_core}
                        onChange={() => handleModuleToggle(module.id, module.is_core)}
                        disabled={module.is_core}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:opacity-50"
                      />
                      <div>
                        <p className="font-medium text-gray-900">
                          {module.name}
                          {module.is_core && (
                            <span className="ml-2 text-xs text-gray-500">(Core)</span>
                          )}
                        </p>
                        {module.description && (
                          <p className="text-sm text-gray-500">{module.description}</p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Team Users */}
        <section className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold">Team Users ({team.users.length})</h2>
            <Link
              to={`/admin/teams/${teamId}/users`}
              className="text-sm text-blue-600 hover:text-blue-700"
            >
              Manage Users →
            </Link>
          </div>
          <div className="space-y-2">
            {team.users.slice(0, 5).map((user) => (
              <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <p className="font-medium text-gray-900">{user.fullName}</p>
                  <p className="text-sm text-gray-500">{user.email}</p>
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  user.role === 'team_admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                }`}>
                  {user.role === 'team_admin' ? 'Admin' : 'Agent'}
                </span>
              </div>
            ))}
            {team.users.length > 5 && (
              <p className="text-sm text-gray-500 text-center pt-2">
                + {team.users.length - 5} more users
              </p>
            )}
          </div>
        </section>

        {/* Submit */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate('/admin/teams')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
          >
            <Save size={20} />
            {isSubmitting ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default TeamDetail;
